/**
 * TaksasiPage
 * 
 * Main page component for Taksasi module
 */

import React, { useCallback } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Flex,
  useToast,
} from '@chakra-ui/react';
import Navbar from '../../../components/Navbar';
import { useTaksasi } from '../hooks/useTaksasi';
import FilterTaksasi from '../components/FilterTaksasi';
import TaksasiTable from '../components/TaksasiTable';
import TaksasiPagination from '../components/TaksasiPagination';
import { exportToExcel } from '../utils/utils';
import TaksasiApiService from '../services/taksasiApiService';
import { TaksasiFilters } from '../types';

/**
 * Main Taksasi Page Component
 */
const TaksasiPage: React.FC = () => {
  const toast = useToast();

  const {
    data,
    stats,
    pagination,
    loading,
    filters,
    setFilters,
    refreshData,
    goToPage,
    setLimit,
  } = useTaksasi();

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback(
    (newFilters: any) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  /**
   * Handle clear filters
   */
  const handleClearFilters = useCallback(() => {
    toast({
      title: 'Filter direset',
      description: 'Semua filter telah dikembalikan ke nilai default',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  }, [toast]);

  /**
   * Handle apply filters
   */
  const handleApplyFilters = useCallback(() => {
    toast({
      title: 'Filter diterapkan',
      // description: 'Data sedang dimuat dengan filter baru',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  }, [toast]);

  /**
   * Handle export to Excel
   */
  const handleExport = useCallback(async (filterParams?: TaksasiFilters) => {
    if (!filterParams?.company_id) {
      return;
    }

    try {
      // Fetch langsung dengan filter dari button click, tanpa update state
      const response = await TaksasiApiService.getList(filterParams);

      if (!response.success || !response.data || response.data.length === 0) {
        toast({
          title: 'Tidak ada data',
          description: 'Tidak ada data untuk diekspor dengan filter ini',
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      // Export langsung dari fetch result, tidak ambil dari state
      const filename = `taksasi-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      exportToExcel(response.data, filename);

      toast({
        title: 'Export berhasil',
        description: `Data berhasil diekspor ke ${filename}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      toast({
        title: 'Export gagal',
        description: err.message || 'Terjadi kesalahan saat mengekspor data',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [toast]);

  /**
   * Handle refresh data
   */
  const handleRefresh = useCallback(() => {
    refreshData();
    toast({
      title: 'Data diperbarui',
      description: 'Data taksasi telah dimuat ulang',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  }, [refreshData, toast]);

  return (
    <Box minH="100vh" bg="gray.50">
      <Navbar />

      <Box flex="1" p={{ base: 4, md: 6 }}>
        <VStack spacing={6} align="stretch">
          {/* Page Header */}
          <Heading size="lg" color="customGreen.500">
            Taksasi
          </Heading>

          {/* Filter Section */}
          <FilterTaksasi
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
            onExport={handleExport}
            onRefresh={handleRefresh}
          />

          {/* Data Table */}
          <TaksasiTable data={data} loading={loading} />

          {/* Pagination */}
          {!loading && data.length > 0 && (
            <TaksasiPagination
              pagination={pagination}
              onPageChange={goToPage}
              onLimitChange={setLimit}
            />
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default TaksasiPage;
